import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { MapPin, Link as LinkIcon, DollarSign, Target, X, UploadCloud, Loader2 } from 'lucide-react';
import { useCreateTransferRequest, useUpdateTransferRequest, useTransferRequest } from '@/hooks/useTransferRequest';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/misc';
import { Select } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { NumberInput } from '@/components/ui/numberInput';
import AddressSelector from '@/components/common/AddressSelector';
import { useUIStore } from '@/store/uiStore';
import { FileUploadDropzone } from '@/components/common';
import { Autocomplete, TextField, Chip } from '@mui/material';
import { imageApi } from '@/api/imageApi';

const AREA_UNITS = [
  { value: 'm²', label: 'm²' },
  { value: 'ft²', label: 'ft²' },
];
const CURRENCY = [
  { value: 'VND', label: 'VND' },
  { value: 'USD', label: 'USD' },
];

const COMMON_CATEGORIES = [
  "Nhà hàng", "Quán cà phê", "Quán nhậu", "Trà sữa", "Tiệm bánh", "Bar/Pub", "Food Court", "Khác"
];

const optionalNumber = z.preprocess(
  (val) => {
    if (val === '' || val === undefined || val === null) return undefined;
    if (typeof val === 'string') val = val.replace(/\D/g, '');
    const num = Number(val);
    return isNaN(num) ? undefined : num;
  },
  z.number().optional()
);

const schema = z.object({
  countryId: z.coerce.number().default(1),
  provinceId: z.coerce.number({ invalid_type_error: 'Vui lòng chọn Tỉnh/Thành phố' }).min(1, 'Vui lòng chọn Tỉnh/Thành phố'),
  districtId: z.coerce.number({ invalid_type_error: 'Vui lòng chọn Quận/Huyện' }).min(1, 'Vui lòng chọn Quận/Huyện'),
  wardId: z.coerce.number({ invalid_type_error: 'Vui lòng chọn Phường/Xã' }).min(1, 'Vui lòng chọn Phường/Xã'),
  budgetMin: z.string()
    .transform(v => v ? String(v).replace(/\D/g, '') : '')
    .transform(v => v ? Number(v) : 0)
    .refine(v => v > 0, { message: 'Ngân sách tối thiểu phải > 0' }),
  budgetMax: z.string()
    .transform(v => v ? String(v).replace(/\D/g, '') : '')
    .transform(v => v ? Number(v) : 0)
    .refine(v => v > 0, { message: 'Ngân sách tối đa phải > 0' }),
  currencyUnit: z.string().default('VND'),

  preferredAreaMin: optionalNumber,
  preferredAreaMax: optionalNumber,
  areaUnit: z.string().default('m²'),
  seatingCapacity: optionalNumber,
  distanceToCityCenter: z.string().optional(),
  preferredCategories: z.array(z.string()).min(1, 'Vui lòng chọn ít nhất 1 loại hình'),
  additionalRequirements: z.string().optional(),
});
type FormData = z.infer<typeof schema>;

interface Props {
  transferId?: string;
  onSuccess?: () => void;
  onCancel?: () => void;
}

interface UploadedImg {
  id: string;
  url: string;
  isPrimary: boolean;
  file?: File;
}

export default function CreateTransferRequestPage({ transferId: propTransferId, onSuccess, onCancel }: Props = {}) {
  const params = useParams();
  const id = propTransferId || params.id;
  const isEditMode = !!id;

  const navigate = useNavigate();
  const { mutate: create, isPending: isCreating } = useCreateTransferRequest();
  const { mutate: update, isPending: isUpdating } = useUpdateTransferRequest();
  const isPending = isCreating || isUpdating;
  const { addToast } = useUIStore();

  const { data: existingData } = useTransferRequest(id || '');

  const [images, setImages] = useState<UploadedImg[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    control,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { countryId: 1, currencyUnit: 'VND', areaUnit: 'm²', preferredCategories: [] },
  });

  useEffect(() => {
    if (isEditMode && existingData) {
      reset({
        countryId: existingData.countryId || 1,
        provinceId: existingData.provinceId || 1,
        districtId: existingData.districtId || 1,
        wardId: existingData.wardId || 1,
        budgetMin: existingData.budgetMin ? String(existingData.budgetMin) : '',
        budgetMax: existingData.budgetMax ? String(existingData.budgetMax) : '',
        currencyUnit: existingData.currencyUnit || 'VND',
        preferredAreaMin: existingData.preferredAreaMin,
        preferredAreaMax: existingData.preferredAreaMax,
        areaUnit: existingData.areaUnit || 'm²',
        seatingCapacity: existingData.seatingCapacity,
        distanceToCityCenter: existingData.distanceToCityCenter,
        preferredCategories: existingData.preferredCategories || [],
        additionalRequirements: existingData.additionalRequirements || '',
      } as any);

      if (existingData.images?.length) {
        setImages(
          existingData.images.map((img) => ({
            id: img.imageId || img.id,
            url: img.image?.publicUrl || '',
            isPrimary: img.isPrimary,
          }))
        );
      }
    }
  }, [isEditMode, existingData, reset]);

  const countryId = watch('countryId');
  const provinceId = watch('provinceId');
  const districtId = watch('districtId');
  const wardId = watch('wardId');

  const onSubmit = async (data: FormData) => {
    if (data.budgetMax < data.budgetMin) {
      addToast('error', 'Ngân sách tối đa phải lớn hơn hoặc bằng ngân sách tối thiểu');
      return;
    }

    if (data.preferredAreaMax && data.preferredAreaMin && data.preferredAreaMax < data.preferredAreaMin) {
      addToast('error', 'Diện tích tối đa phải lớn hơn hoặc bằng diện tích tối thiểu');
      return;
    }

    try {
      setIsUploading(true);
      const finalImages = [];
      for (const img of images) {
        if (img.file) {
          const res = await imageApi.upload(img.file);
          if (res && res.data) {
            finalImages.push({ id: res.data.id, isPrimary: img.isPrimary });
          }
        } else {
          finalImages.push({ id: img.id, isPrimary: img.isPrimary });
        }
      }

      const payload = {
        ...data,
        budgetMin: Number(data.budgetMin),
        budgetMax: Number(data.budgetMax),
        preferredAreaMin: Number(data.preferredAreaMin || 0),
        preferredAreaMax: Number(data.preferredAreaMax || 0),
        images: finalImages,
      };

      if (isEditMode && id) {
        update({ id, data: payload as any }, {
          onSuccess: () => {
            if (onSuccess) onSuccess();
            else navigate(`/nhu-cau-sang-nhuong/${id}`);
          }
        });
      } else {
        create(payload as any, {
          onSuccess: (res: any) => {
            if (onSuccess) onSuccess();
            else navigate(`/nhu-cau-sang-nhuong`);
          }
        });
      }
    } catch (err: any) {
      if (err?.response?.status === 413) {
        addToast('error', 'Ảnh tải lên quá lớn (vượt giới hạn 1MB của máy chủ). Vui lòng chọn ảnh nhỏ hơn.');
      } else {
        addToast('error', 'Lỗi khi tải ảnh lên. Vui lòng thử lại.');
      }
    } finally {
      setIsUploading(false);
    }
  };

  const onInvalid = (errors: any) => {
    console.log(errors);
    addToast('error', 'Vui lòng kiểm tra lại các trường thông tin bị thiếu hoặc không hợp lệ.');
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const newLocalImages: UploadedImg[] = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (!file.type.startsWith('image/')) {
        addToast('error', `File ${file.name} không phải là hình ảnh hợp lệ.`);
        continue;
      }

      if (file.size > 1 * 1024 * 1024) {
        addToast('error', `Ảnh ${file.name} vượt quá dung lượng cho phép. Vui lòng chọn ảnh dưới 1MB.`);
        continue;
      }

      newLocalImages.push({
        id: Math.random().toString(36).substring(7),
        url: URL.createObjectURL(file),
        isPrimary: images.length === 0 && i === 0 && newLocalImages.length === 0,
        file,
      });
    }

    setImages((prev) => [...prev, ...newLocalImages]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeImage = (id: string) => {
    setImages((prev) => {
      const filtered = prev.filter((img) => img.id !== id);
      // reset primary if the removed one was primary
      if (filtered.length > 0 && !filtered.find((img) => img.isPrimary)) {
        filtered[0].isPrimary = true;
      }
      return filtered;
    });
  };

  const setPrimaryImage = (id: string) => {
    setImages((prev) => prev.map((img) => ({ ...img, isPrimary: img.id === id })));
  };

  return (
    <div className="max-w-3xl mx-auto animate-fade-in pb-10">
      <div className="flex items-center gap-3 mb-8">
        <div>
          <h1 className="font-display font-bold text-3xl" style={{ color: 'var(--text-1)' }}>
            {isEditMode ? 'Cập nhật nhu cầu' : 'Đăng nhu cầu sang nhượng'}
          </h1>
          <p className="text-base mt-1" style={{ color: 'var(--text-3)' }}>
            Điền đầy đủ thông tin để thu hút các cơ hội sang nhượng phù hợp
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit, onInvalid)} className="space-y-6">
        {/* ── Khu vực mong muốn ── */}
        <Card title="Khu vực mong muốn" icon={<MapPin size={18} />}>
          <AddressSelector
            required
            countryId={countryId}
            provinceId={provinceId}
            districtId={districtId}
            wardId={wardId}
            onCountryChange={(id) => { setValue('countryId', id); setValue('provinceId', 0); setValue('districtId', 0); setValue('wardId', 0); }}
            onProvinceChange={(id) => { setValue('provinceId', id); setValue('districtId', 0); setValue('wardId', 0); }}
            onDistrictChange={(id) => { setValue('districtId', id); setValue('wardId', 0); }}
            onWardChange={(id) => setValue('wardId', id)}
            errors={{
              countryId: errors.countryId?.message,
              provinceId: errors.provinceId?.message,
              districtId: errors.districtId?.message,
              wardId: errors.wardId?.message,
            }}
          />
        </Card>

        {/* ── Ngân sách đầu tư ── */}
        <Card title="Ngân sách đầu tư" icon={<DollarSign size={18} />}>
          <div className="grid grid-cols-3 gap-5">
            <NumberInput label="Từ *" placeholder="500.000.000" {...register('budgetMin')} error={errors.budgetMin?.message} />
            <NumberInput label="Đến *" placeholder="2.000.000.000" {...register('budgetMax')} error={errors.budgetMax?.message} />
            <Select label="Đơn vị tiền" options={CURRENCY} {...register('currencyUnit')} value={watch('currencyUnit')} error={errors.currencyUnit?.message} />

          </div>
          <div className="mt-4">
          </div>
        </Card>

        {/* ── Yêu cầu chi tiết ── */}
        <Card title="Yêu cầu chi tiết" icon={<Target size={18} />}>
          <div className="mb-5">
            <p className="text-sm font-semibold mb-2" style={{ color: 'var(--text-3)' }}>Loại hình kinh doanh mong muốn *</p>
            <Controller
              name="preferredCategories"
              control={control}
              render={({ field }) => (
                <Autocomplete
                  {...field}
                  multiple
                  freeSolo
                  options={COMMON_CATEGORIES}
                  onChange={(_, data) => field.onChange(data)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Chọn hoặc nhập loại hình (Enter để thêm)"
                      error={!!errors.preferredCategories}
                      helperText={errors.preferredCategories?.message}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: '12px',
                          bgcolor: '#f8fafc',
                          '& fieldset': { borderColor: '#e2e8f0' },
                          '&:hover fieldset': { borderColor: '#f97316' },
                          '&.Mui-focused fieldset': { borderColor: '#f97316' },
                        }
                      }}
                    />
                  )}
                />
              )}
            />
          </div>

          <div className="grid grid-cols-2 gap-5 mb-5">
            <Input label="Diện tích mong muốn từ" type="number" placeholder="80" {...register('preferredAreaMin')} error={errors.preferredAreaMin?.message} />
            <Input label="Đến" type="number" placeholder="300" {...register('preferredAreaMax')} error={errors.preferredAreaMax?.message} />
          </div>
          <div className="grid grid-cols-2 gap-5 mb-5">
            <Select label="Đơn vị diện tích" options={AREA_UNITS} {...register('areaUnit')} value={watch('areaUnit')} error={errors.areaUnit?.message} />
            <Input label="Sức chứa tối thiểu (chỗ)" type="number" placeholder="20" {...register('seatingCapacity')} error={errors.seatingCapacity?.message} />
          </div>

          <div className="mb-5">
            <Input label="Cách trung tâm tối đa (km)" type="text" placeholder="VD: 5" {...register('distanceToCityCenter')} error={errors.distanceToCityCenter?.message} />
          </div>

          <Textarea
            label="Yêu cầu thêm"
            rows={4}
            placeholder="VD: Cần mặt tiền đường lớn, có bãi đỗ xe rộng..."
            {...register('additionalRequirements')}
            error={errors.additionalRequirements?.message}
          />
        </Card>

        {/* ── Hình ảnh minh hoạ ── */}
        <Card title="Hình ảnh minh hoạ (Tuỳ chọn)" icon={<LinkIcon size={18} />}>
          <div className="space-y-4">
            <FileUploadDropzone
              onFileSelect={(files) => handleFileUpload({ target: { files } } as any)}
              isUploading={isUploading}
              multiple
              accept="image/*,.pdf"
              title="Nhấn để chọn hoặc kéo thả ảnh vào đây"
              subtitle="Hỗ trợ JPG, PNG, PDF. Tối đa 5MB"
            />

            {images.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-4">
                {images.map((img) => (
                  <div key={img.id} className={cn(
                    "relative aspect-video rounded-xl overflow-hidden border-2 group",
                    img.isPrimary ? "border-orange-500 shadow-md" : "border-gray-200"
                  )}>
                    <img src={img.url} alt="" className="w-full h-full object-cover" />

                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                      <button
                        type="button"
                        onClick={() => removeImage(img.id)}
                        className="w-8 h-8 rounded-full bg-white/20 hover:bg-red-500 text-white flex items-center justify-center backdrop-blur-sm transition-colors"
                        title="Xóa ảnh"
                      >
                        <X size={14} />
                      </button>
                    </div>

                    {img.isPrimary ? (
                      <div className="absolute top-2 left-2 px-2 py-1 bg-orange-500 text-white text-[10px] font-bold rounded shadow-sm">
                        Ảnh chính
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setPrimaryImage(img.id)}
                        className="absolute top-2 left-2 px-2 py-1 bg-black/50 hover:bg-black/80 text-white text-[10px] font-bold rounded opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        Đặt làm ảnh chính
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </Card>

        {/* Navigation */}
        <div className="flex gap-4 justify-end pt-4 border-t" style={{ borderColor: 'var(--border-c)' }}>
          <Button type="button" variant="ghost" onClick={() => onCancel ? onCancel() : navigate('/nhu-cau-sang-nhuong')} className="px-8 font-bold">
            Hủy
          </Button>
          <Button type="submit" variant="brand" loading={isPending || isUploading} className="px-8 font-bold text-base shadow-lg shadow-orange-500/20">
            {isEditMode ? 'Lưu thay đổi' : 'Đăng nhu cầu'}
          </Button>
        </div>
      </form>
    </div>
  );
}

function Card({ title, icon, children }: { title: string; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="rounded-3xl border p-6 md:p-8 space-y-4 bg-white shadow-sm" style={{ borderColor: 'var(--border-c)' }}>
      <h2 className="font-display font-bold text-lg flex items-center gap-2 pb-4 border-b" style={{ color: 'var(--text-1)', borderColor: 'var(--border-c)' }}>
        <div className="w-8 h-8 rounded-lg bg-orange-50 flex items-center justify-center text-orange-500">
          {icon}
        </div>
        {title}
      </h2>
      <div className="pt-2">
        {children}
      </div>
    </div>
  );
}
